import React from "react";

// Source
import StoreLocalization from "../store/Localization";
import StorePreference from "../store/Preference";
import ControllerAlert from "./Alert";
import * as HelperSrc from "../HelperSrc";
import * as ApiLocalization from "../api/Localization";
import * as ModelLocalization from "../model/Localization";
import * as ModelCustomLocalization from "../model/custom/Localization";
import * as ModelPreference from "../model/Preference";

export default class ControllerLocalization {
    // Variable
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);
    private storeMain: ModelLocalization.Istore<ModelCustomLocalization.Idata>;
    private storageMainData = React.useRef<ModelCustomLocalization.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementBody = React.useRef<HTMLElement | null>(null);
    private elementMenuButton = React.useRef<HTMLButtonElement | null>(null);
    private elementViewMain = React.useRef<HTMLElement | null>(null);
    private elementHtml = React.useRef<HTMLElement | null>(null);

    private isRendered = React.useRef<boolean>(false);

    getStorageMain: ModelCustomLocalization.Idata | undefined;
    setStorageMain: React.Dispatch<React.SetStateAction<ModelCustomLocalization.Idata | undefined>>;

    getElementMenuButton: HTMLButtonElement | null;
    setElementMenuButton: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;

    getIsLocalizationOpen: boolean;
    setIsLocalizationOpen: React.Dispatch<React.SetStateAction<boolean>>;

    // Method
    constructor(isRendered: boolean) {
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();
        this.storeMain = new StoreLocalization();
        this.storageMainData.current = this.storeMain.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getStorageMain, setStorageMain] = React.useState<ModelCustomLocalization.Idata | undefined>(this.storageMainData.current);
        this.getStorageMain = getStorageMain;
        this.setStorageMain = setStorageMain;

        const [getElementMenuButton, setMenuButtonElement] = React.useState<HTMLButtonElement | null>(null);
        this.getElementMenuButton = getElementMenuButton;
        this.setElementMenuButton = setMenuButtonElement;

        const [getIsLocalizationOpen, setIsLocalizationOpen] = React.useState<boolean>(false);
        this.getIsLocalizationOpen = getIsLocalizationOpen;
        this.setIsLocalizationOpen = setIsLocalizationOpen;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            let isMounted = true;

            HelperSrc.domObserver(".menu", () => {
                this.initializeHtmlElement(isMounted);
            });

            this.handleStorage(isMounted);

            HelperSrc.storageEventListener("localization", () => {
                this.handleStorage(isMounted);
            });
            HelperSrc.storageEventListener("preference", () => {
                this.handleStorage(isMounted);
            });

            return () => {
                isMounted = false;
            };
        }, []);

        React.useEffect(() => {
            this.storagePreferenceData.current = this.storePreference.storageGet();
            this.storageMainData.current = this.storeMain.storageGet();
        }, [this.storagePreferenceData.current, this.storageMainData.current]);
    };

    clickOpenLocalization = (): void => {
        this.setIsLocalizationOpen(true);
    };

    closeLocalization = (): void => {
        this.setIsLocalizationOpen(false);
    };

    clickChange = (language: string, country: string): void => {
        const storagePreferenceData = {
            ...this.storagePreferenceData.current,
            country,
            language
        } as ModelPreference.Idata;

        this.storePreference.storageSet(storagePreferenceData);

        ApiLocalization.dataList((response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.storeMain.storageSet(response.data.result as unknown as ModelCustomLocalization.Idata);

            if (this.elementBody.current) {
                this.elementBody.current.removeAttribute("class");
                this.elementBody.current.classList.add(language);
            }

            if (this.elementHtml.current) {
                this.elementHtml.current.setAttribute("lang", language);
            }
        });

        this.setIsLocalizationOpen(false);
    };

    private initializeHtmlElement = (isMounted: boolean): void => {
        if (!isMounted) return;

        if (!this.elementViewMain.current) {
            this.elementBody.current = document.querySelector<HTMLElement>("body");
            this.elementMenuButton.current = document.querySelector<HTMLButtonElement>("[name='locale_button']");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".menu");
            this.elementHtml.current = document.querySelector<HTMLElement>("html");

            this.setElementMenuButton(this.elementMenuButton.current);
        }
    };

    private handleStorage = (isMounted: boolean): void => {
        if (!isMounted) return;

        this.storageMainData.current = this.storeMain.storageGet();
        this.setStorageMain(this.storageMainData.current);
    };
}
